import { useCallback, useEffect, useRef, useState } from "react";
import {
	CacheEntry,
	fetchCached,
	isFresh,
	readCache,
} from "../utils/requestCache";

interface CachedResource<T> {
	data: T | undefined;
	/** When `data` was fetched (ms since epoch), or undefined if nothing is cached. */
	updatedAt: number | undefined;
	/** A request for this key is in flight, including background refreshes. */
	loading: boolean;
	/** The last request failed. Cached data, if any, is still returned. */
	error: boolean;
	/** Fetch again even if the cached copy is still fresh. */
	reload: () => Promise<void>;
}

/**
 * Serves `key` from the request cache straight away and fetches it in the background once the
 * cached copy is older than `ttlMs`. Pass `null` as the key to skip fetching entirely, for data
 * this user never sees or that is only loaded on demand.
 */
const useCachedResource = <T>(
	key: string | null,
	fetcher: () => Promise<T>,
	ttlMs: number
): CachedResource<T> => {
	const [entry, setEntry] = useState<CacheEntry<T> | undefined>(() =>
		key ? readCache<T>(key) : undefined
	);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);

	// The fetcher closes over render-time values; always call the latest one.
	const fetcherRef = useRef(fetcher);
	fetcherRef.current = fetcher;
	const keyRef = useRef(key);
	keyRef.current = key;

	const load = useCallback(async (requestKey: string) => {
		setLoading(true);
		setError(false);
		try {
			const result = await fetchCached(requestKey, () =>
				fetcherRef.current()
			);
			if (keyRef.current === requestKey) setEntry(result);
		} catch {
			if (keyRef.current === requestKey) setError(true);
		} finally {
			if (keyRef.current === requestKey) setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!key) {
			setEntry(undefined);
			setLoading(false);
			return;
		}
		const cached = readCache<T>(key);
		setEntry(cached);
		if (!isFresh(cached, ttlMs)) {
			load(key);
		} else {
			setLoading(false);
			setError(false);
		}
	}, [key, ttlMs, load]);

	const reload = useCallback(async () => {
		if (keyRef.current) await load(keyRef.current);
	}, [load]);

	return {
		data: entry?.data,
		updatedAt: entry?.storedAt,
		loading,
		error,
		reload,
	};
};

export default useCachedResource;
